import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Button, Drawer, IconButton, Paper, Stack, Switch, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

export const DASHBOARD_SECTIONS = [
  { id: "stats", label: "Summary cards" },
  { id: "needsAttention", label: "Needs attention" },
  { id: "upcomingClosings", label: "Upcoming closings" },
  { id: "role", label: "Your role" },
  { id: "volume", label: "Volume" },
  { id: "activeOrders", label: "Active orders" }
] as const;

export type DashboardSectionId = (typeof DASHBOARD_SECTIONS)[number]["id"];

export interface DashboardLayout {
  order: DashboardSectionId[];
  hidden: DashboardSectionId[];
}

// The layout belongs to whoever is viewing, so the same one applies on every user's dashboard
const STORAGE_KEY = "userDashboard.layout.v1";
const sectionIds = DASHBOARD_SECTIONS.map((section) => section.id);
const defaultLayout: DashboardLayout = { order: [...sectionIds], hidden: [] };

// Drops sections that no longer exist and appends ones added since the layout was saved
const normalizeLayout = (layout: Partial<DashboardLayout> | null): DashboardLayout => {
  const isKnown = (id: unknown): id is DashboardSectionId => sectionIds.includes(id as DashboardSectionId);
  const order = (layout?.order ?? []).filter(isKnown);
  return {
    order: [...order, ...sectionIds.filter((id) => !order.includes(id))],
    hidden: (layout?.hidden ?? []).filter(isKnown)
  };
};

export const useDashboardLayout = () => {
  const [layout, setLayout] = useState<DashboardLayout>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return normalizeLayout(saved ? JSON.parse(saved) : null);
    } catch {
      return defaultLayout;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    } catch {
      // Storage can be unavailable (private windows, blocked site data); the layout just won't be remembered
    }
  }, [layout]);

  const resetLayout = useCallback(() => setLayout(defaultLayout), []);
  const isDefault = JSON.stringify(layout) === JSON.stringify(defaultLayout);

  return { layout, setLayout, resetLayout, isDefault };
};

export default function DashboardLayoutDrawer({
  open,
  onClose,
  layout,
  onChange,
  onReset,
  isDefault
}: {
  open: boolean;
  onClose: () => void;
  layout: DashboardLayout;
  onChange: (layout: DashboardLayout) => void;
  onReset: () => void;
  isDefault: boolean;
}) {
  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || destination.index === source.index) return;
    const order = [...layout.order];
    const [moved] = order.splice(source.index, 1);
    order.splice(destination.index, 0, moved);
    onChange({ ...layout, order });
  };

  const toggleSection = (id: DashboardSectionId, visible: boolean) =>
    onChange({
      ...layout,
      hidden: visible ? layout.hidden.filter((hiddenId) => hiddenId !== id) : [...layout.hidden, id]
    });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 360 } } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6">Customize dashboard</Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, flexGrow: 1, overflow: "auto" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag sections to reorder them, or switch them off to hide them. Saved in this browser and used on every user's
          dashboard.
        </Typography>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="dashboard-sections">
            {(provided) => (
              <Stack spacing={1} ref={provided.innerRef} {...provided.droppableProps}>
                {layout.order.map((id, index) => {
                  const section = DASHBOARD_SECTIONS.find((s) => s.id === id)!;
                  const isVisible = !layout.hidden.includes(id);
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(dragProvided, snapshot) => (
                        <Paper
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          variant="outlined"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            pl: 1,
                            pr: 0.5,
                            py: 0.5,
                            bgcolor: snapshot.isDragging ? "action.selected" : "background.paper",
                            boxShadow: snapshot.isDragging ? 8 : "none"
                          }}
                        >
                          <Box
                            {...dragProvided.dragHandleProps}
                            aria-label={`Drag to move ${section.label}`}
                            sx={{ display: "flex", color: "text.disabled", cursor: "grab" }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ flexGrow: 1, color: isVisible ? "text.primary" : "text.disabled" }}
                          >
                            {section.label}
                          </Typography>
                          <Switch
                            size="small"
                            checked={isVisible}
                            onChange={(_, checked) => toggleSection(id, checked)}
                            inputProps={{ "aria-label": `Show ${section.label}` }}
                          />
                        </Paper>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onReset} disabled={isDefault}>
          Reset to default
        </Button>
      </Box>
    </Drawer>
  );
}
